import {Json} from '../../../components/JsonObjectCard';
import {RequestExample} from '../../../components/RequestExampleCard';
import {RequestDefinitionData} from '../../../components/RequestDefinition';
import {Attribute} from '../../../components/ObjectAttribute';

const createTransferOrderRequest: RequestExample = {
    title: {
        verb: 'POST',
        endpointUrl: '/api/transfer-orders',
    },
    curl:
        `curl --location \\
--request POST '<YOUR SERVER>/api/transfer-orders' \\
--header 'Content-Type: application/json' \\
--header 'Authorization: Bearer <TOKEN>' \\
--data-raw '<TO JSON>'
`,
};

const createTransferOrderResponse: Json = {
    title: 'RESPONSE',
    json: {
        'id': 1,
        'number': 'TO-00001',
        'status': 'Entered',
        'type': 'Ship',
        'fromLocationGroup': {
            'id': 1,
            'name': 'Main Warehouse'
        },
        'toLocationGroup': {
            'id': 2,
            'name': 'East Warehouse'
        },
        'carrier': {
            'id': 5,
            'name': 'USPS',
            'service': {
                'id': 3,
                'name': 'Priority Mail'
            }
        },
        'shipFromAddress': {
            'name': 'Main Warehouse',
            'street': '100 Main St',
            'city': 'Salt Lake City',
            'state': 'UT',
            'postalCode': '84101',
            'country': 'UNITED STATES'
        },
        'shipToAddress': {
            'name': 'East Warehouse',
            'street': '200 East Blvd',
            'city': 'Denver',
            'state': 'CO',
            'postalCode': '80201',
            'country': 'UNITED STATES'
        },
        'note': '',
        'ownerIsFrom': true,
        'revisionNumber': 1,
        'lastModified': {
            'username': 'admin',
            'dateLastModified': '2024-03-01T10:00:00.000-0700'
        },
        'dateCreated': '2024-03-01T08:00:00.000-0700',
        'dateIssued': '2024-03-02T08:00:00.000-0700',
        'dateScheduled': '2024-03-05T00:00:00.000-0700',
        'dateConfirmed': '2024-03-01T12:00:00.000-0700',
        'dateCompleted': '2024-03-06T08:00:00.000-0700',
        'toItems': [
            {
                'id': 101,
                'part': {
                    'id': 28,
                    'name': 'B201',
                    'description': 'Premium Brake Cables'
                },
                'type': {
                    'id': 10,
                    'name': 'Send'
                },
                'status': 'Entered',
                'uom': {
                    'id': 1,
                    'name': 'Each',
                    'abbreviation': 'ea'
                },
                'lineNumber': 1,
                'note': '',
                'quantity': '10',
                'quantityFulfilled': '1',
                'quantityPicked': '1',
                'unitCost': '12.50',
                'dateScheduled': '2024-03-05T00:00:00.000-0700',
                'dateLastFulfilled': '2024-03-06T08:00:00.000-0700'
            }
        ],
        'customFields': [
            {
                'id': 22,
                'name': 'Custom',
                'value': '',
                'type': 'Text'
            }
        ]
    },
};

const createTransferOrderAttributes: Attribute[] = [
    {
        name: 'number',
        type: 'string',
        optional: true,
        description: 'The transfer order number. Required if TO auto-numbering is disabled.',
    },
    {
        name: 'type',
        type: 'string: \'Ship\' | \'Move\'',
        description: 'The type of transfer order. Ship requires address fields; Move ignores them.',
    },
    {
        name: 'status',
        type: 'string',
        optional: true,
        description: 'The order status. Must be Entered if provided; defaults to Entered.',
    },
    {
        name: 'fromLocationGroup',
        type: 'reference object',
        description: 'The source location group.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The unique identification number.',
            },
        ],
    },
    {
        name: 'toLocationGroup',
        type: 'reference object',
        description: 'The destination location group.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The unique identification number.',
            },
        ],
    },
    {
        name: 'shipFromAddress',
        type: 'address object',
        optional: true,
        description: 'The ship-from address. Required for Ship type; ignored for Move type.',
        children: [
            { name: 'name', type: 'string', optional: true, description: 'The address name.' },
            { name: 'street', type: 'string', optional: true, description: 'The street address.' },
            { name: 'city', type: 'string', optional: true, description: 'The city.' },
            { name: 'state', type: 'string', optional: true, description: 'The state code or name.' },
            { name: 'postalCode', type: 'string', optional: true, description: 'The postal code.' },
            { name: 'country', type: 'string', optional: true, description: 'The country name or code.' },
        ],
    },
    {
        name: 'shipToAddress',
        type: 'address object',
        optional: true,
        description: 'The ship-to address. Required for Ship type; ignored for Move type.',
        children: [
            { name: 'name', type: 'string', optional: true, description: 'The address name.' },
            { name: 'street', type: 'string', optional: true, description: 'The street address.' },
            { name: 'city', type: 'string', optional: true, description: 'The city.' },
            { name: 'state', type: 'string', optional: true, description: 'The state code or name.' },
            { name: 'postalCode', type: 'string', optional: true, description: 'The postal code.' },
            { name: 'country', type: 'string', optional: true, description: 'The country name or code.' },
        ],
    },
    {
        name: 'carrier',
        type: 'reference object',
        optional: true,
        description: 'The carrier for the transfer order.',
        children: [
            { name: 'id', type: 'integer', description: 'The unique identification number.' },
            {
                name: 'service',
                type: 'reference object',
                optional: true,
                description: 'The carrier service.',
                children: [
                    { name: 'id', type: 'integer', description: 'The carrier service ID.' },
                ],
            },
        ],
    },
    {
        name: 'note',
        type: 'string',
        optional: true,
        description: 'A note on the transfer order.',
    },
    {
        name: 'ownerIsFrom',
        type: 'boolean',
        optional: true,
        description: 'Whether the source location group owns the inventory during transfer.',
    },
    {
        name: 'dateScheduled',
        type: 'date',
        optional: true,
        description: 'The scheduled fulfillment date.',
    },
    {
        name: 'dateConfirmed',
        type: 'date',
        optional: true,
        description: 'The date the order was confirmed.',
    },
    {
        name: 'toItems',
        type: 'list',
        description: 'A list of transfer order items. At least one item is required.',
        children: [
            {
                name: 'part',
                type: 'reference object',
                description: 'The part to transfer.',
                children: [
                    { name: 'id', type: 'integer', description: 'The unique identification number.' },
                ],
            },
            {
                name: 'uom',
                type: 'reference object',
                optional: true,
                description: 'The unit of measure. Defaults to the part\'s default UOM.',
                children: [
                    { name: 'id', type: 'integer', description: 'The unique identification number.' },
                ],
            },
            {
                name: 'type',
                type: 'reference object',
                optional: true,
                description: 'The item type. Defaults to Send.',
                children: [
                    { name: 'id', type: 'integer', description: 'The unique identification number.' },
                ],
            },
            {
                name: 'quantity',
                type: 'quantity',
                description: 'The quantity to transfer. Must be greater than zero.',
            },
            {
                name: 'note',
                type: 'string',
                optional: true,
                description: 'A note on the line item.',
            },
            {
                name: 'unitCost',
                type: 'money',
                optional: true,
                description: 'The unit cost of the item. Applied only for non-inventory or shipping parts on Ship-type transfer orders; ignored otherwise. Omitting it leaves the cost at zero.',
            },
            {
                name: 'dateScheduled',
                type: 'date',
                optional: true,
                description: 'The scheduled fulfillment date for this item.',
            },
        ],
    },
];

const data: RequestDefinitionData = {
    id: 'create-transfer-order',
    title: 'Create a transfer order',
    description: 'Creates a new transfer order in Entered status. The type field determines which fields are required: ' +
        'Ship orders require shipFromAddress and shipToAddress; Move orders ignore address fields. ' +
        'At least one line item is required. The owner location group — the from group when ownerIsFrom is true, ' +
        'otherwise the to group — must be one you have write access to; otherwise the request returns 403 with the ' +
        'message "Access denied. User does not have access to the owner location group." The non-owner location group ' +
        'may be any active location group, including one you cannot access.',
    endpoint: {
        verb: 'POST',
        path: '/transfer-orders',
        tooltip: 'Create a transfer order',
    },
    attributes: createTransferOrderAttributes,
    requestObject: createTransferOrderRequest,
    responseObject: createTransferOrderResponse,
};

export default data;
